// @ts-nocheck
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import MensagensTab from "@/components/MensagensTab";

interface Projeto {
  id: string;
  nome: string;
  objetivo: string;
  prazo: string;
  valor_maximo: number;
  created_at: string;
  updated_at: string;
  cliente?: { nome: string };
  empresa?: { razao_social: string };
  fornecedor?: { razao_social: string };
  local?: { nome: string };
}

export default function ProjetoView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [projeto, setProjeto] = useState<Projeto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadProjeto();
    }
  }, [id]);

  const loadProjeto = async () => {
    try {
      const { data, error } = await supabase
        .from("projetos")
        .select(`
          *,
          cliente:clientes(nome),
          empresa:empresas(razao_social),
          fornecedor:fornecedores(razao_social),
          local:locais(nome)
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProjeto(data);
      } else {
        toast({
          title: "Erro",
          description: "Projeto não encontrado",
          variant: "destructive"
        });
        navigate("/intranet/propostas/projetos");
      }
    } catch (error) {
      console.error("Erro ao carregar projeto:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do projeto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  if (!projeto) {
    return <div className="p-6">Projeto não encontrado</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/intranet/propostas/projetos")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{projeto.nome}</h1>
            <p className="text-muted-foreground">Visualização do projeto</p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/intranet/propostas/projetos/edit/${projeto.id}`)}
        >
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      <Tabs defaultValue="dados-basicos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
          <TabsTrigger value="relacionamentos">Relacionamentos</TabsTrigger>
          <TabsTrigger value="mensagens">Mensagens</TabsTrigger>
        </TabsList>

        <TabsContent value="dados-basicos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Nome</label>
                  <p className="text-base font-medium">{projeto.nome}</p>
                </div>

                {projeto.objetivo && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Objetivo</label>
                    <p className="text-base">{projeto.objetivo}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Prazo</label>
                  <p className="text-base">
                    {projeto.prazo ? formatDate(projeto.prazo) : "Não definido"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Valor Máximo</label>
                  <p className="text-base">
                    {projeto.valor_maximo ? formatCurrency(projeto.valor_maximo) : "Não definido"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Criação</label>
                  <p className="text-base">{formatDate(projeto.created_at)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Última Atualização</label>
                  <p className="text-base">{formatDate(projeto.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="relacionamentos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Vínculos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Cliente</label>
                  <p className="text-base">
                    {projeto.cliente?.nome ? (
                      <Badge variant="secondary">{projeto.cliente.nome}</Badge>
                    ) : (
                      "Não vinculado"
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Empresa</label>
                  <p className="text-base">
                    {projeto.empresa?.razao_social ? (
                      <Badge variant="secondary">{projeto.empresa.razao_social}</Badge>
                    ) : (
                      "Não vinculada"
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fornecedor</label>
                  <p className="text-base">
                    {projeto.fornecedor?.razao_social ? (
                      <Badge variant="secondary">{projeto.fornecedor.razao_social}</Badge>
                    ) : (
                      "Não vinculado"
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Local</label>
                  <p className="text-base">
                    {projeto.local?.nome ? (
                      <Badge variant="secondary">{projeto.local.nome}</Badge>
                    ) : (
                      "Não vinculado"
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mensagens" className="space-y-4">
          <MensagensTab tela="projetos" idRegistro={projeto.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}